import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { UpOrDown } from '@hygh-vybe/lib';
import { useAuth } from '../contexts/AuthContext';
import { useRatingsAndReviews, type ReviewData } from '../hooks/useRatingsAndReviews';
import { getCollectionStatus } from '../utils/collectionStatus';
import { Toast, type ToastMessage, createToastId } from './Toast';
import './ProductRatingReview.css';

const MAX_REVIEW_CHARS = 2000;

interface ProductRatingReviewProps {
  productId: string;
  productName?: string;
}

export function ProductRatingReview({ productId }: ProductRatingReviewProps) {
  const { user, token } = useAuth();
  const userId = user?.email ?? null;
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const {
    existingRating,
    ratingLoading,
    reviews,
    reviewsLoading,
    hasMoreReviews,
    loadMoreReviews,
    reviewSubmitting,
    editReview,
    deleteReview,
    deleteSubmitting,
    submitRatingAndReview,
    combinedSubmitting,
  } = useRatingsAndReviews(productId, token, userId);

  // Form state
  const [ratingValue, setRatingValue] = useState<number>(0);
  const [buzzScore, setBuzzScore] = useState<number>(5);
  const [upOrDown, setUpOrDown] = useState<UpOrDown | null>(null);
  const [reviewText, setReviewText] = useState('');
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);

  // Sync initial values when existing rating loads
  const [initialSynced, setInitialSynced] = useState(false);
  if (existingRating && !initialSynced) {
    setRatingValue(existingRating.ratingValue);
    setBuzzScore(existingRating.buzzScore);
    setUpOrDown(existingRating.upOrDown);
    setInitialSynced(true);
  }

  const addToast = useCallback((text: string, type: 'success' | 'error') => {
    setToasts((prev) => [...prev, { id: createToastId(), text, type }]);
  }, []);

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const handleStarClick = useCallback((e: React.MouseEvent<HTMLButtonElement>, star: number) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const isLeftHalf = x < rect.width / 2;
    const value = isLeftHalf ? star - 0.5 : star;
    setRatingValue(value);
    setFormErrors((prev) => {
      const next = { ...prev };
      delete next.ratingValue;
      return next;
    });
  }, []);

  const handleSubmit = useCallback(async () => {
    const errors: Record<string, string> = {};

    if (ratingValue === 0) {
      errors.ratingValue = 'Please select a star rating';
    }
    if (!upOrDown) {
      errors.upOrDown = 'Please select an effect';
    }
    if (reviewText.trim().length < 10) {
      errors.reviewText = 'Review must be at least 10 characters';
    }
    if (reviewText.length > MAX_REVIEW_CHARS) {
      errors.reviewText = `Review must be at most ${MAX_REVIEW_CHARS} characters`;
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setFormErrors({});

    try {
      await submitRatingAndReview({
        ratingValue,
        buzzScore,
        upOrDown: upOrDown!,
        reviewText: reviewText.trim(),
        isFirstReview: reviews.length === 0,
      });
      addToast('Thanks for sharing your experience!', 'success');
      setSubmitted(true);
      setReviewText('');
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Failed to submit', 'error');
    }
  }, [ratingValue, buzzScore, upOrDown, reviewText, submitRatingAndReview, addToast]);

  const handleReviewEdit = useCallback(
    async (text: string) => {
      try {
        await editReview(text);
        addToast('Review updated!', 'success');
      } catch (err) {
        addToast(err instanceof Error ? err.message : 'Failed to update review', 'error');
      }
    },
    [editReview, addToast]
  );

  const handleReviewDelete = useCallback(async () => {
    try {
      await deleteReview();
      addToast('Review deleted', 'success');
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Failed to delete review', 'error');
    }
  }, [deleteReview, addToast]);

  const showForm = user && !submitted && !existingRating;

  // The submitted rating to display after submission
  const submittedRating = submitted
    ? { ratingValue, buzzScore, upOrDown }
    : existingRating
      ? { ratingValue: existingRating.ratingValue, buzzScore: existingRating.buzzScore, upOrDown: existingRating.upOrDown }
      : null;

  return (
    <section className="product-rating-review" aria-label="Rate and Review">
      {/* First Review CTA — shown above everything when no reviews exist */}
      {reviews.length === 0 && !reviewsLoading && (
        <div className="product-rating-review__first-review-cta">
          <span className="product-rating-review__first-badge">🏆</span>
          <strong>Be the first to review this drink!</strong>
          <p>First reviewers earn a 🥇 badge on their review and in their Stash.</p>
        </div>
      )}

      {/* Combined Rate & Review Section */}
      <div className="product-rating-review__section">
        <h3 className="product-rating-review__heading">Rate &amp; Review</h3>

        {!user ? (
          <p className="product-rating-review__login-prompt">
            <Link to={`/auth/login?redirect=/products/${productId}`}>
              Sign in to rate and review
            </Link>
          </p>
        ) : ratingLoading ? (
          <p className="product-rating-review__loading">Loading...</p>
        ) : showForm ? (
          <div className="product-rating-review__form" role="form" aria-label="Rate and review this product">
            {/* User avatar */}
            {user.pictureUrl && (
              <div className="product-rating-review__form-user">
                <img
                  src={user.pictureUrl}
                  alt={user.displayName || 'You'}
                  className="product-rating-review__form-avatar"
                  referrerPolicy="no-referrer"
                />
                <span className="product-rating-review__form-name">{user.displayName}</span>
              </div>
            )}
            {/* Review Text — shown FIRST */}
            <div className="product-rating-review__field">
              <textarea
                className={`product-rating-review__textarea${formErrors.reviewText ? ' product-rating-review__textarea--error' : ''}`}
                placeholder="Share your experience..."
                value={reviewText}
                onChange={(e) => {
                  setReviewText(e.target.value);
                  setFormErrors((prev) => {
                    const next = { ...prev };
                    delete next.reviewText;
                    return next;
                  });
                }}
                rows={3}
                maxLength={MAX_REVIEW_CHARS + 100}
                disabled={combinedSubmitting}
                aria-label="Review text (optional)"
              />
              <div className="product-rating-review__textarea-footer">
                <span className="product-rating-review__textarea-hint">Required — minimum 10 characters</span>
                <span className={`product-rating-review__char-count${reviewText.length > MAX_REVIEW_CHARS ? ' product-rating-review__char-count--over' : ''}`}>
                  {reviewText.length}/{MAX_REVIEW_CHARS}
                </span>
              </div>
              {formErrors.reviewText && (
                <p className="product-rating-review__field-error" role="alert">{formErrors.reviewText}</p>
              )}
            </div>

            {/* Star Rating */}
            <div className="product-rating-review__field">
              <div className="product-rating-review__field-header">
                <span className="product-rating-review__label">★ Star Rating</span>
              </div>
              <p className="product-rating-review__subtitle">How would you rate this drink overall? (1-5)</p>
              <div className="product-rating-review__stars" role="group" aria-label="Star rating">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    className={`product-rating-review__star ${ratingValue >= star ? 'product-rating-review__star--full' : ratingValue >= star - 0.5 ? 'product-rating-review__star--half' : ''}`}
                    onClick={(e) => handleStarClick(e, star)}
                    aria-label={`${star} star${star > 1 ? 's' : ''}`}
                    disabled={combinedSubmitting}
                  >
                    <svg viewBox="0 0 24 24" className="product-rating-review__star-svg" aria-hidden="true">
                      <defs>
                        <linearGradient id={`prt-half-fill-${star}`}>
                          <stop offset="50%" stopColor="var(--color-primary)" />
                          <stop offset="50%" stopColor="transparent" />
                        </linearGradient>
                      </defs>
                      <path
                        d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
                        fill={
                          ratingValue >= star
                            ? 'var(--color-primary)'
                            : ratingValue >= star - 0.5
                              ? `url(#prt-half-fill-${star})`
                              : 'none'
                        }
                        stroke="var(--color-primary)"
                        strokeWidth="1.5"
                      />
                    </svg>
                  </button>
                ))}
                {ratingValue > 0 && (
                  <span className="product-rating-review__stars-value">{ratingValue.toFixed(1)}</span>
                )}
              </div>
              {formErrors.ratingValue && (
                <p className="product-rating-review__field-error" role="alert">{formErrors.ratingValue}</p>
              )}
            </div>

            {/* Buzz Score */}
            <div className="product-rating-review__field">
              <div className="product-rating-review__field-header">
                <span className="product-rating-review__label">⚡ Buzz Score</span>
              </div>
              <p className="product-rating-review__subtitle">How strong was the effect? (1 = barely felt it, 10 = very strong)</p>
              <div className="product-rating-review__buzz" role="group" aria-label="Buzz score">
                <span className="product-rating-review__buzz-value">⚡ {buzzScore}</span>
                <input
                  type="range"
                  min="1"
                  max="10"
                  step="1"
                  value={buzzScore}
                  onChange={(e) => setBuzzScore(Number(e.target.value))}
                  className="product-rating-review__buzz-slider"
                  disabled={combinedSubmitting}
                  aria-label="Buzz score"
                  aria-valuemin={1}
                  aria-valuemax={10}
                  aria-valuenow={buzzScore}
                />
              </div>
            </div>

            {/* Up/Down/Neutral Toggle */}
            <div className="product-rating-review__field">
              <div className="product-rating-review__field-header">
                <span className="product-rating-review__label">↑↓ Effect</span>
                <span className="product-rating-review__required-badge">Required</span>
              </div>
              <p className="product-rating-review__subtitle">Energizing (Up), relaxing (Down), or somewhere in between (Neutral)?</p>
              <div className="product-rating-review__updown" role="group" aria-label="Effect direction" aria-required="true">
                <button
                  type="button"
                  className={`product-rating-review__toggle product-rating-review__toggle--up${upOrDown === UpOrDown.Up ? ' product-rating-review__toggle--active' : ''}`}
                  onClick={() => {
                    setUpOrDown(upOrDown === UpOrDown.Up ? null : UpOrDown.Up);
                    setFormErrors((prev) => {
                      const next = { ...prev };
                      delete next.upOrDown;
                      return next;
                    });
                  }}
                  aria-pressed={upOrDown === UpOrDown.Up}
                  aria-label="Energizing effect (Up)"
                  disabled={combinedSubmitting}
                >
                  ↑ Up
                </button>
                <button
                  type="button"
                  className={`product-rating-review__toggle product-rating-review__toggle--neutral${upOrDown === UpOrDown.Neutral ? ' product-rating-review__toggle--active' : ''}`}
                  onClick={() => {
                    setUpOrDown(upOrDown === UpOrDown.Neutral ? null : UpOrDown.Neutral);
                    setFormErrors((prev) => {
                      const next = { ...prev };
                      delete next.upOrDown;
                      return next;
                    });
                  }}
                  aria-pressed={upOrDown === UpOrDown.Neutral}
                  aria-label="Neutral effect"
                  disabled={combinedSubmitting}
                >
                  — Neutral
                </button>
                <button
                  type="button"
                  className={`product-rating-review__toggle product-rating-review__toggle--down${upOrDown === UpOrDown.Down ? ' product-rating-review__toggle--active' : ''}`}
                  onClick={() => {
                    setUpOrDown(upOrDown === UpOrDown.Down ? null : UpOrDown.Down);
                    setFormErrors((prev) => {
                      const next = { ...prev };
                      delete next.upOrDown;
                      return next;
                    });
                  }}
                  aria-pressed={upOrDown === UpOrDown.Down}
                  aria-label="Relaxing effect (Down)"
                  disabled={combinedSubmitting}
                >
                  ↓ Down
                </button>
              </div>
              {formErrors.upOrDown && (
                <p className="product-rating-review__field-error" role="alert">{formErrors.upOrDown}</p>
              )}
            </div>

            {/* Submit */}
            <button
              type="button"
              className="product-rating-review__submit"
              onClick={handleSubmit}
              disabled={combinedSubmitting || !upOrDown || ratingValue === 0 || reviewText.trim().length < 10}
            >
              {combinedSubmitting ? 'Submitting...' : 'Submit Review'}
            </button>
          </div>
        ) : submittedRating ? (
          <div className="product-rating-review__submitted">
            <p className="product-rating-review__submitted-title">Your rating</p>
            <div className="product-rating-review__submitted-values">
              <span className="product-rating-review__submitted-item">
                ★ {submittedRating.ratingValue.toFixed(1)}
              </span>
              <span className="product-rating-review__submitted-item">
                ⚡ {submittedRating.buzzScore}
              </span>
              {submittedRating.upOrDown && (
                <span className={`product-rating-review__submitted-item product-rating-review__submitted-item--${submittedRating.upOrDown.toLowerCase()}`}>
                  {submittedRating.upOrDown === UpOrDown.Up ? '↑ Up' : submittedRating.upOrDown === UpOrDown.Down ? '↓ Down' : '↔ Neutral'}
                </span>
              )}
            </div>
          </div>
        ) : (
          <p className="product-rating-review__edit-hint">
            Thanks for sharing your experience!
          </p>
        )}
      </div>

      {/* Review/Rating History — always shown regardless of auth */}
      <div className="product-rating-review__section">
        <h3 className="product-rating-review__heading">Reviews</h3>

        {reviewsLoading && reviews.length === 0 ? (
          <p className="product-rating-review__loading">Loading reviews...</p>
        ) : reviews.length > 0 ? (
          <ReviewHistoryList
            reviews={reviews}
            currentUserId={userId}
            currentUserPictureUrl={user?.pictureUrl}
            onEdit={handleReviewEdit}
            onDelete={handleReviewDelete}
            editSubmitting={reviewSubmitting}
            deleteSubmitting={deleteSubmitting}
          />
        ) : (
          <>
            <p className="product-rating-review__empty">No reviews yet — be the first!</p>
          </>
        )}

        {hasMoreReviews && (
          <button
            type="button"
            className="product-rating-review__load-more"
            onClick={loadMoreReviews}
            disabled={reviewsLoading}
          >
            {reviewsLoading ? 'Loading...' : 'Load more reviews'}
          </button>
        )}
      </div>

      <Toast messages={toasts} onDismiss={dismissToast} />
    </section>
  );
}

/**
 * Review history list — shows each review with display name, star rating,
 * buzz score, up/down indicator, review text, location, and relative time.
 */
interface ReviewHistoryListProps {
  reviews: ReviewData[];
  currentUserId: string | null;
  currentUserPictureUrl?: string;
  currentUserReviewCount?: number;
  onEdit: (text: string) => Promise<void>;
  onDelete: () => Promise<void>;
  editSubmitting: boolean;
  deleteSubmitting: boolean;
}

function ReviewHistoryList({
  reviews,
  currentUserId,
  currentUserPictureUrl,
  currentUserReviewCount,
  onEdit,
  onDelete,
  editSubmitting,
  deleteSubmitting,
}: ReviewHistoryListProps) {
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [menuUserId, setMenuUserId] = useState<string | null>(null);

  const handleEditStart = useCallback((review: ReviewData) => {
    setEditingUserId(review.userId);
    setEditText(review.reviewText);
    setMenuUserId(null);
  }, []);

  const handleEditSubmit = useCallback(async () => {
    if (editText.trim().length < 10) return;
    await onEdit(editText.trim());
    setEditingUserId(null);
    setEditText('');
  }, [editText, onEdit]);

  const handleDelete = useCallback(async () => {
    setMenuUserId(null);
    await onDelete();
  }, [onDelete]);

  return (
    <div className="product-rating-review__review-list" role="list" aria-label="Product reviews">
      {reviews.map((review) => {
        const isOwn = currentUserId === review.userId;
        const isEditing = editingUserId === review.userId;
        const showMenu = menuUserId === review.userId;

        return (
          <article key={review.userId} className="product-rating-review__review-item" role="listitem">
            <div className="product-rating-review__review-header">
              {isOwn && currentUserPictureUrl ? (
                <img
                  src={currentUserPictureUrl}
                  alt={review.displayName || 'Profile'}
                  className="product-rating-review__review-avatar-img"
                  referrerPolicy="no-referrer"
                />
              ) : (
              <div className="product-rating-review__review-avatar" aria-hidden="true">
                {(review.displayName || 'A').charAt(0).toUpperCase()}
              </div>
              )}
              <div className="product-rating-review__review-meta">
                <span className="product-rating-review__review-author">
                  {review.displayName || 'Anonymous'}
                  {isOwn && currentUserReviewCount != null && (() => {
                    const s = getCollectionStatus(currentUserReviewCount);
                    return s.tier !== 'none' ? (
                      <span className="product-rating-review__review-badge">{s.icon}</span>
                    ) : null;
                  })()}
                </span>
                <div className="product-rating-review__review-scores">
                  {review.ratingValue != null && review.ratingValue > 0 && (
                    <span className="product-rating-review__review-score-item">★ {review.ratingValue.toFixed(1)}</span>
                  )}
                  {review.buzzScore != null && review.buzzScore > 0 && (
                    <span className="product-rating-review__review-score-item">⚡ {review.buzzScore}</span>
                  )}
                  {review.upOrDown && (
                    <span className={`product-rating-review__review-score-item product-rating-review__review-score-item--${review.upOrDown.toLowerCase()}`}>
                      {review.upOrDown === 'Up' ? '↑ Up' : review.upOrDown === 'Down' ? '↓ Down' : '↕ Neutral'}
                    </span>
                  )}
                  <span className="product-rating-review__review-score-item product-rating-review__review-score-item--location">
                    {review.location || '—'}
                  </span>
                </div>
                <time
                  className="product-rating-review__review-time"
                  dateTime={review.updatedAt || review.createdAt}
                >
                  {formatTimeAgo(review.updatedAt || review.createdAt)}
                </time>
              </div>
              {isOwn && !isEditing && (
                <button
                  type="button"
                  className="product-rating-review__menu-trigger"
                  onClick={() => setMenuUserId(showMenu ? null : review.userId)}
                  aria-label="Review actions"
                  aria-expanded={showMenu}
                >
                  ⋯
                </button>
              )}
            </div>

            {isEditing ? (
              <div className="product-rating-review__edit-form">
                <textarea
                  className="product-rating-review__textarea"
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  rows={3}
                  disabled={editSubmitting}
                />
                <div className="product-rating-review__edit-actions">
                  <button
                    type="button"
                    className="product-rating-review__edit-cancel"
                    onClick={() => setEditingUserId(null)}
                    disabled={editSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="product-rating-review__edit-save"
                    onClick={handleEditSubmit}
                    disabled={editSubmitting || editText.trim().length < 10}
                  >
                    {editSubmitting ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            ) : (
              review.reviewText && (
                <p className="product-rating-review__review-text">{review.reviewText}</p>
              )
            )}

            {showMenu && isOwn && (
              <div className="product-rating-review__menu" role="menu">
                {review.reviewText && (
                  <button
                    type="button"
                    className="product-rating-review__menu-item"
                    onClick={() => handleEditStart(review)}
                    role="menuitem"
                  >
                    Edit
                  </button>
                )}
                <button
                  type="button"
                  className="product-rating-review__menu-item product-rating-review__menu-item--danger"
                  onClick={handleDelete}
                  disabled={deleteSubmitting}
                  role="menuitem"
                >
                  {deleteSubmitting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            )}
          </article>
        );
      })}
    </div>
  );
}

function formatTimeAgo(isoDate: string): string {
  const now = Date.now();
  const then = new Date(isoDate).getTime();
  const diffSeconds = Math.floor((now - then) / 1000);

  if (diffSeconds < 60) return 'just now';
  if (diffSeconds < 3600) {
    const mins = Math.floor(diffSeconds / 60);
    return `${mins} minute${mins > 1 ? 's' : ''} ago`;
  }
  if (diffSeconds < 86400) {
    const hrs = Math.floor(diffSeconds / 3600);
    return `${hrs} hour${hrs > 1 ? 's' : ''} ago`;
  }
  if (diffSeconds < 604800) {
    const days = Math.floor(diffSeconds / 86400);
    return `${days} day${days > 1 ? 's' : ''} ago`;
  }

  return new Date(isoDate).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}
